import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { GetLanguageString } from "../../helper/Components";
import {
  customOption,
  customSingleValue,
  LoadingSpinner,
  CheckPermission,
} from "../../../constants/const";
import { IoArrowBackSharp } from "react-icons/io5";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { GetCustomStyles, compressImageFile, getFileNameWithoutExtension } from "../../helper/Method";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import swal from "sweetalert";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";
import { CashAccountBalanceCard, parseBalanceNumber } from "../BalanceCards";

// ---------------- Reusable Components ----------------
const CustomSelect = ({
  label,
  name,
  options = [],
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options || []}
      name={name}
      value={value}
      className="basic-select no-drag"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || !options || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      styles={{
        control: (provided, state) => ({
          ...provided,
          borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#e5e6e7",
          "&:hover": {
            borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#d2d2d2",
          },
        }),
      }}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      invalid={!!error}
      {...rest}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Helper Functions ----------------
const isSpecialCategory = (categoryCode) => {
  return (
    categoryCode === "return_loan_individual" ||
    categoryCode === "loan_individual" ||
    categoryCode === "return_loan_from_markaz" ||
    categoryCode === "maktaba_payment_to_markaz" ||
    (categoryCode && categoryCode.includes("markaz"))
  );
};

const isSingleRowCategory = (categoryCode, categoryLabel = "") => {
  if (!categoryCode && !categoryLabel) return false;
  const code = (categoryCode || "").toLowerCase();
  const label = (categoryLabel || "").toLowerCase();
  return (
    code === "maktaba_payment_to_markaz" ||
    label === "maktaba payment to markaz" ||
    code === "return_loan_from_markaz" ||
    code === "return_loan_markaz" ||
    code === "return_loan_to_markaz" ||
    label.includes("loan return to markaz") ||
    label.includes("loan return from markaz")
  );
};

const categorySupportsQtyPrice = (categoryCode) => {
  return (
    categoryCode === "asset" ||
    categoryCode === "maktaba"
  );
};

const findMiscEquipmentAccount = (accounts = []) => {
  if (!accounts || accounts.length === 0) return null;
  return (
    accounts.find(
      (acc) =>
        acc.value === 5695 ||
        acc.value === "5695" ||
        acc.code === 5695 ||
        acc.code === "5695" ||
        (acc.label &&
          (acc.label.includes("5695") ||
            acc.label.toLowerCase().includes("miscellaneous equipment")))
    ) || null
  );
};

// ---------------- Main Component ----------------
const ExpenseFromCashInHand = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");
  // ---------- State ----------
  const [loading, setLoading] = useState({
    source: false,
    submit: false,
  });

  const [financeCategories, setFinanceCategories] = useState([]);
  const [balanceData, setBalanceData] = useState(null);
  const [expenseAmountLimit, setExpenseAmountLimit] = useState(25000);
  const shuraThreshold = unitTypeName === "Halqa" ? (expenseAmountLimit >= 50000 ? 50000 : Infinity) : 20000;

  const [formData, setFormData] = useState({
    date: new Date(),
    memo: "",
    entries: [
      {
        selectedCategory: null,
        financeAccount: null,
        dimension2: null,
        accounts: [],
        dimensions: [],
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
        shuraNotes: "",
        serialNumber: "",
        isLifeLessThan1Year: false,
        isPriceLessThan10k: false,
      },
    ],
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const MAX_SIZE = 1500 * 1024; // 1500 KB limit enforced by Finance Application
      const selected = [];
      const oversizedNonImages = [];

      for (const f of Array.from(e.target.files)) {
        let processedFile = f;
        if (f.type && f.type.startsWith("image/") && f.size > MAX_SIZE) {
          try {
            processedFile = await compressImageFile(f, 1400 * 1024);
          } catch (err) {
            console.error("Image compression error:", err);
          }
        }

        if (processedFile.size > MAX_SIZE) {
          oversizedNonImages.push(f.name);
        } else {
          selected.push({
            file: processedFile,
            customName: getFileNameWithoutExtension(f.name),
            id: Math.random().toString(36).substr(2, 9),
          });
        }
      }

      if (oversizedNonImages.length > 0) {
        swal({
          title: "File Size Limit Exceeded",
          text: `The following file(s) exceed the maximum size of 1500 KB (1.5 MB):\n${oversizedNonImages.join("\n")}`,
          icon: "warning",
          button: "OK",
        });
      }

      if (selected.length > 0) {
        setUploadedFiles((prev) => [...prev, ...selected]);
        setErrors((prev) => ({ ...prev, files: "" }));
      }
      e.target.value = "";
    }
  };

  const removeFile = (indexToRemove) => {
    setUploadedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleFileNameChange = (index, newName) => {
    setUploadedFiles((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, customName: newName } : item))
    );
  };

  // ---------- Helpers ----------
  const fetchAccountsAndDimensions = (index, categoryId) => {
    if (!categoryId) return;

    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              const accounts = res.data || [];
              const categoryCode = entry.selectedCategory?.label1;
              const isAsset = categoryCode === "asset";
              const isMisc =
                isAsset &&
                (entry.isLifeLessThan1Year || entry.isPriceLessThan10k);
              let selectedAcc = null;
              if (isMisc) {
                selectedAcc =
                  findMiscEquipmentAccount(accounts) || {
                    value: 5695,
                    label: "5695 - Miscellaneous Equipment",
                  };
              } else {
                selectedAcc = accounts?.[0] || null;
              }
              return {
                ...entry,
                accounts,
                financeAccount: selectedAcc,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);

    ApiFinance()
      .GetFinanceSubCategoryDimensionsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              return {
                ...entry,
                dimensions: res.data || [],
                dimension2: res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);
  };

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  const handleEntryChange = (index, field, value) => {
    let newAmount = "";
    const currentEntry = formData.entries[index];
    if (currentEntry) {
      if (field === "amount") {
        newAmount = value;
      } else {
        const categoryCode = currentEntry.selectedCategory?.label1;
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);
        if (isQtyPrice) {
          const q = field === "quantity" ? parseFloat(value) : parseFloat(currentEntry.quantity);
          const p = field === "price" ? parseFloat(value) : parseFloat(currentEntry.price);
          if (!isNaN(q) && !isNaN(p)) {
            newAmount = (q * p).toString();
          }
        } else {
          newAmount = currentEntry.amount;
        }
      }
    }

    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: value };

          if (field === "selectedCategory") {
            updated.financeAccount = null;
            updated.dimension2 = null;
            updated.accounts = [];
            updated.dimensions = [];
            updated.serialNumber = "";
            updated.isLifeLessThan1Year = false;
            updated.isPriceLessThan10k = false;
            const isQtyPrice = categorySupportsQtyPrice(value?.label1);
            if (!isQtyPrice) {
              updated.quantity = "";
              updated.price = "";
            }
            if (value?.value) {
              fetchAccountsAndDimensions(index, value.value);
            }
          }

          // Calculate amount if quantity or price changes
          const categoryCode = updated.selectedCategory?.label1;
          if (categorySupportsQtyPrice(categoryCode)) {
            if (field === "quantity" || field === "price" || field === "selectedCategory") {
              const q = parseFloat(updated.quantity);
              const p = parseFloat(updated.price);
              if (!isNaN(q) && !isNaN(p)) {
                updated.amount = (q * p).toString();
              } else {
                updated.amount = "";
              }
            }
          }

          return updated;
        }
        return entry;
      });

      return { ...prev, entries: updatedEntries };
    });
    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (!updatedEntries[index]) {
        updatedEntries[index] = {};
      }

      if (field === "selectedCategory") {
        updatedEntries[index] = {
          selectedCategory: "",
          financeAccount: "",
          dimension2: "",
          amount: "",
          quantity: "",
          price: "",
          serialNumber: "",
        };
      } else {
        updatedEntries[index] = {
          ...updatedEntries[index],
          [field]: "",
        };
      }

      if (newAmount && newAmount.trim() !== "") {
        const parsedAmount = parseFloat(newAmount);
        if (!isNaN(parsedAmount) && parsedAmount > expenseAmountLimit) {
          updatedEntries[index].amount = `${t("you_cannot_spend_above_this_amount_in_cash_in_hand")} (${expenseAmountLimit})`;
        }
      }

      return { ...prev, entries: updatedEntries };
    });
  };

  const handleCheckboxChange = (index, field, checked) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: checked };
          const isAsset = updated.selectedCategory?.label1 === "asset";
          const isMisc =
            isAsset &&
            (updated.isLifeLessThan1Year || updated.isPriceLessThan10k);
          if (isMisc) {
            const matched = findMiscEquipmentAccount(updated.accounts);
            updated.financeAccount =
              matched || {
                value: 5695,
                label: "5695 - Miscellaneous Equipment",
              };
          } else if (isAsset) {
            if (
              updated.financeAccount?.value === 5695 ||
              updated.financeAccount?.label?.includes("5695")
            ) {
              updated.financeAccount = updated.accounts?.[0] || null;
            }
          }
          return updated;
        }
        return entry;
      });
      return { ...prev, entries: updatedEntries };
    });

    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (updatedEntries[index]) {
        updatedEntries[index] = {
          ...updatedEntries[index],
          financeAccount: "",
        };
      }
      return { ...prev, entries: updatedEntries };
    });
  };

  const addEntry = () => {
    const hasSingleRowCategory = formData.entries.some((r) =>
      isSingleRowCategory(r.selectedCategory?.label1, r.selectedCategory?.label)
    );
    if (hasSingleRowCategory) {
      return;
    }
    const defaultCategory = financeCategories[0] || null;
    const nextIndex = formData.entries.length;
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          selectedCategory: defaultCategory,
          financeAccount: null,
          dimension2: null,
          accounts: [],
          dimensions: [],
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
          shuraNotes: "",
          serialNumber: "",
          isLifeLessThan1Year: false,
          isPriceLessThan10k: false,
        },
      ],
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: [...prev.entries, {}]
        };
      }
      return prev;
    });
    if (defaultCategory?.value) {
      fetchAccountsAndDimensions(nextIndex, defaultCategory.value);
    }
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: prev.entries.filter((_, idx) => idx !== index)
        };
      }
      return prev;
    });
  };

  // Helper function to pad numbers to 4 digits with leading zeros
  const padTo4Digits = (value) => {
    if (!value) return "0000";
    return String(value).padStart(4, "0");
  };

  const formatSelectListModel = (item) => {
    if (!item) return null;
    return {
      value: parseInt(item.value, 10) || 0,
      label: item.label ? String(item.label) : "",
      label1: item.label1 ? String(item.label1) : "",
    };
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const totalAmount = getTotalAmount(formData.entries);

    const entriesPayload = formData.entries.map((entry) => {
      const categoryCode = entry.selectedCategory?.label1;
      const isQtyPrice = categorySupportsQtyPrice(categoryCode);
      let fallbackMemo = entry.lineMemo || formData.memo;
      const parsedAmount = parseFloat(entry.amount) || 0;
      if (parsedAmount >= shuraThreshold && entry.shuraNotes?.trim()) {
        fallbackMemo = fallbackMemo?.trim()
          ? `${fallbackMemo.trim()} - Shura Approval Notes(${entry.shuraNotes.trim()})`
          : `Shura Approval Notes(${entry.shuraNotes.trim()})`;
      }
      return {
        category: formatSelectListModel(entry.selectedCategory),
        financeAccount: formatSelectListModel(entry.financeAccount),
        dimension2: formatSelectListModel(entry.dimension2),
        amount: parsedAmount,
        lineMemo: isQtyPrice
          ? `${padTo4Digits(entry.quantity)} x ${padTo4Digits(entry.price)}${fallbackMemo ? ` (${fallbackMemo})` : ""}`
          : fallbackMemo,
      };
    });

    const loanRow = formData.entries.find(e => e.selectedCategory?.label1 === "return_loan_individual");
    const serialNumber = loanRow ? loanRow.serialNumber : "";

    let finalMemo = formData.memo;
    const firstShuraRow = formData.entries.find(e => (parseFloat(e.amount) || 0) >= shuraThreshold && e.shuraNotes?.trim());
    if (firstShuraRow) {
      finalMemo = formData.memo?.trim()
        ? `${formData.memo.trim()} - Shura Approval Notes(${firstShuraRow.shuraNotes.trim()})`
        : `Shura Approval Notes(${firstShuraRow.shuraNotes.trim()})`;
    }

    const payload = {
      financeAccount: entriesPayload[0]?.financeAccount ?? null,
      dimension2: entriesPayload[0]?.dimension2 ?? null,
      category: entriesPayload[0]?.category ?? null,
      date: formData.date,
      amount: parseFloat(totalAmount) || 0,
      serialNumber: serialNumber,
      memo: finalMemo,
      unitId: loggedInUnitId,
      entries: entriesPayload,
    };

    const formDataToSend = new FormData();
    uploadedFiles.forEach((item) => {
      let fileName = item.customName?.trim() || item.file.name;
      const originalExt = item.file.name.includes(".") ? item.file.name.substring(item.file.name.lastIndexOf(".")) : "";
      if (originalExt && !fileName.toLowerCase().endsWith(originalExt.toLowerCase())) {
        fileName = `${fileName}${originalExt}`;
      }
      formDataToSend.append("files", item.file, fileName);
    });

    Object.keys(payload).forEach((key) => {
      if (key === "entries") {
        payload.entries.forEach((entry, i) => {
          if (entry.category) {
            formDataToSend.append(`entries[${i}].category.value`, entry.category.value);
            formDataToSend.append(`entries[${i}].category.label`, entry.category.label);
            formDataToSend.append(`entries[${i}].category.label1`, entry.category.label1 || "");
          }
          if (entry.financeAccount) {
            formDataToSend.append(`entries[${i}].financeAccount.value`, entry.financeAccount.value);
            formDataToSend.append(`entries[${i}].financeAccount.label`, entry.financeAccount.label);
          }
          if (entry.dimension2) {
            formDataToSend.append(`entries[${i}].dimension2.value`, entry.dimension2.value);
            formDataToSend.append(`entries[${i}].dimension2.label`, entry.dimension2.label);
          }
          formDataToSend.append(`entries[${i}].amount`, entry.amount);
          formDataToSend.append(`entries[${i}].lineMemo`, entry.lineMemo || "");
        });
      } else if (payload[key] !== null && payload[key] !== undefined) {
        if (key === "date") {
          const dateObj = new Date(payload[key]);
          const isoDate = !isNaN(dateObj.getTime()) ? dateObj.toISOString() : payload[key];
          formDataToSend.append("date", isoDate);
        } else if (typeof payload[key] === "object" && payload[key].value !== undefined) {
          formDataToSend.append(`${key}.value`, payload[key].value);
          formDataToSend.append(`${key}.label`, payload[key].label);
        } else {
          formDataToSend.append(key, payload[key]);
        }
      }
    });

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .AddExpenseFromCashInHand(formDataToSend)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === 200) {
          swal({
            title: "Success",
            text:
              (typeof data?.message === "string" && data.message ? t(data.message) : "") ||
              t("funds_expense_added_successfully"),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text:
            typeof data?.message === "string"
              ? data.message
              : "Failed to add Expense",
          icon: "error",
          buttons: "OK",
        });

        setLoading((prev) => ({ ...prev, submit: false }));
      })
      .catch((err) => {
        let errorMessage = "Unable to connect to server. Please try again.";
        if (typeof err?.response?.data === "string" && err.response.data.trim()) {
          errorMessage = err.response.data;
        } else if (err?.response?.data?.message && typeof err.response.data.message === "string") {
          errorMessage = err.response.data.message;
        } else if (err?.response?.data?.title && typeof err.response.data.title === "string") {
          errorMessage = err.response.data.title;
        } else if (err?.message && typeof err.message === "string") {
          errorMessage = err.message;
        }

        swal({
          title: "Error",
          text: String(errorMessage),
          icon: "error",
          buttons: "OK",
        });
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  // Fetch finance categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinanceLocal()
      .GetCashToAmanatTransferData(loggedInUnitId)
      .then((res) => {
        setBalanceData(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch balance data", err);
      });

    ApiFinance()
      .GetExpenseAmountLimit()
      .then((res) => {
        const limit = parseFloat(res.data);
        if (!isNaN(limit)) {
          setExpenseAmountLimit(limit);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch expense amount limit", err);
      });

    ApiFinance()
      .GetFinanceSubCategoriesListByType("expense")
      .then((res) => {
        const filtered = (res.data || []).filter((category) => {
          if (
            category.label1 === "maktaba_payment_to_markaz" ||
            category.label1 === "help_from_muqami_tanzeem" ||
            category.label1 === "help_to_unit" ||
            category.label1.includes("help")
          ) {
            return false;
          }
          if (
            unitTypeName !== "Halqa" &&
            (category.label1?.toLowerCase().includes("maktaba") ||
              (category.label || category.name || category.category || "").toLowerCase().includes("maktaba"))
          ) {
            return false;
          }
          return true;
        });
        const formatted = filtered.map((c) => ({
          value: c.value || c.id,
          label: c.label || c.category,
          label1: c.label1 || c.code,
        }));
        setFinanceCategories(formatted);

        if (formatted.length > 0) {
          const defaultCategory = formatted[0];
          setFormData((prev) => ({
            ...prev,
            entries: [
              {
                selectedCategory: defaultCategory,
                financeAccount: null,
                dimension2: null,
                accounts: [],
                dimensions: [],
                amount: "",
                quantity: "",
                price: "",
                lineMemo: "",
                serialNumber: "",
                isLifeLessThan1Year: false,
                isPriceLessThan10k: false,
              },
            ],
          }));
          fetchAccountsAndDimensions(0, defaultCategory.value);
        }
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Failed to load categories",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, source: false })));
  }, [unitTypeName, loggedInUnitId]);

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};

    const entriesErrors = [];
    let hasInvalidEntry = false;

    for (let i = 0; i < formData.entries.length; i++) {
      const entry = formData.entries[i];
      const entryErrors = {};

      if (!entry.selectedCategory) {
        entryErrors.selectedCategory = t("common_field_required_error");
        hasInvalidEntry = true;
      } else {
        const categoryCode = entry.selectedCategory.label1;
        const isSpecial = isSpecialCategory(categoryCode);
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);

        // Validate Amount: empty vs <= 0 (only when not auto-calculated)
        if (!isQtyPrice) {
          if (!entry.amount || entry.amount.trim() === "") {
            entryErrors.amount = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedAmount = parseFloat(entry.amount);
            if (isNaN(parsedAmount) || parsedAmount <= 0) {
              entryErrors.amount = t("amount_must_be_greater_than_zero");
              hasInvalidEntry = true;
            } else if (parsedAmount > expenseAmountLimit) {
              entryErrors.amount = `${t("you_cannot_spend_above_this_amount_in_cash_in_hand")} (${expenseAmountLimit})`;
              hasInvalidEntry = true;
            }
          }
        }

        if (!isSpecial) {
          if (!entry.financeAccount) {
            entryErrors.financeAccount = t("common_field_required_error");
            hasInvalidEntry = true;
          }
          if (!entry.dimension2 && (entry.dimensions || []).length > 0) {
            entryErrors.dimension2 = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if (isQtyPrice) {
          // Validate Quantity
          if (!entry.quantity || entry.quantity.trim() === "") {
            entryErrors.quantity = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedQty = parseFloat(entry.quantity);
            if (isNaN(parsedQty) || parsedQty <= 0) {
              entryErrors.quantity = t("quantity_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }

          // Validate Price
          if (!entry.price || entry.price.trim() === "") {
            entryErrors.price = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedPrice = parseFloat(entry.price);
            if (isNaN(parsedPrice) || parsedPrice <= 0) {
              entryErrors.price = t("price_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }

          if (!entryErrors.quantity && !entryErrors.price) {
            const parsedAmount = parseFloat(entry.amount);
            if (!isNaN(parsedAmount) && parsedAmount > expenseAmountLimit) {
              entryErrors.amount = `${t("you_cannot_spend_above_this_amount_in_cash_in_hand")} (${expenseAmountLimit})`;
              hasInvalidEntry = true;
            }
          }
        }

        if (categoryCode === "return_loan_individual" || categoryCode === "loan_individual") {
          if (!entry.serialNumber || !entry.serialNumber.trim()) {
            entryErrors.serialNumber = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if ((parseFloat(entry.amount) || 0) >= shuraThreshold) {
          if (!entry.shuraNotes || !entry.shuraNotes.trim()) {
            entryErrors.shuraNotes = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }
      }

      entriesErrors.push(entryErrors);
    }

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      hasInvalidEntry = true;
    }

    if (!uploadedFiles || uploadedFiles.length === 0) {
      temp.files = t("expense_receipt_attachment_required");
      hasInvalidEntry = true;
    }

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    const hasSingleRowCategory = formData.entries.some(
      (r) => isSingleRowCategory(r.selectedCategory?.label1, r.selectedCategory?.label)
    );
    const totalRows = formData.entries.length;
    const isInvalidMultiRowState = hasSingleRowCategory && totalRows > 1;

    if (isInvalidMultiRowState) {
      const isMaktaba = formData.entries.some(r => (r.selectedCategory?.label1 || "").toLowerCase() === "maktaba_payment_to_markaz");
      temp.singleRowWarning = isMaktaba ? t("maktaba_payment_single_record_warning") : t("loan_return_markaz_single_record_warning");
      hasInvalidEntry = true;
    }

    const totalExpenseAmount = parseFloat(getTotalAmount(formData.entries)) || 0;
    const effectiveCashBalance = parseBalanceNumber(
      balanceData?.cash_effective_balance ?? balanceData?.cashEffectiveBalance ?? balanceData?.cashAccountBalance
    );
    const isOverEffectiveBalance =
      effectiveCashBalance > 0 && totalExpenseAmount > effectiveCashBalance;

    if (isOverEffectiveBalance) {
      temp.amount = `Total expense exceeds Effective Balance (Rs ${effectiveCashBalance.toLocaleString()})`;
    }

    temp.entries = hasInvalidEntry ? entriesErrors : [];

    setErrors({
      ...temp,
    });

    return !hasInvalidEntry && !temp.date && !isOverEffectiveBalance;
  };

  // ---------- Render ----------
  const totalExpenseAmount = parseFloat(getTotalAmount(formData.entries)) || 0;
  const effectiveCashBalance = parseBalanceNumber(
    balanceData?.cash_effective_balance ?? balanceData?.cashEffectiveBalance ?? balanceData?.cashAccountBalance
  );
  const isOverEffectiveBalance =
    effectiveCashBalance > 0 && totalExpenseAmount > effectiveCashBalance;

  const hasSingleRowCategory = formData.entries.some(
    (r) => isSingleRowCategory(r.selectedCategory?.label1, r.selectedCategory?.label)
  );
  const totalRows = formData.entries.length;
  const isInvalidMultiRowState = hasSingleRowCategory && totalRows > 1;
  const isMaktabaSingle = formData.entries.some(r => (r.selectedCategory?.label1 || "").toLowerCase() === "maktaba_payment_to_markaz");
  const singleRowWarningKey = isMaktabaSingle ? "maktaba_payment_single_record_warning" : "loan_return_markaz_single_record_warning";

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>{<GetLanguageString props="finance_expense_cash_in_hand" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          {loading.source || loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <CashAccountBalanceCard
                  balance={balanceData?.cashAccountBalance}
                  effectiveBalance={balanceData?.cash_effective_balance ?? balanceData?.cashEffectiveBalance ?? balanceData?.cashAccountBalance}
                  ledgerBalance={balanceData?.cash_ledger_balance ?? balanceData?.cashLedgerBalance ?? balanceData?.cashAccountBalance}
                  pendingHolds={balanceData?.cash_pending_holds ?? balanceData?.cashPendingHolds ?? 0}
                  loading={!balanceData}
                  colProps={{ xs: "12", md: "12", className: "mb-3" }}
                />
              </Row>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date).minDate}
                      maxDate={getMinMaxDates(formData.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
                <Col md="4">
                  <CustomInput
                    name="totalAmount"
                    label="finance_other_income_total_amount"
                    value={getTotalAmount(formData.entries)}
                    readOnly
                    disabled
                    error={errors.amount}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="6">
                  <FormGroup className="mb-0">
                    <Label className="col-form-label">
                      <GetLanguageString props="upload_file" /> / <GetLanguageString props="attached_files" /> <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      multiple
                      name="files"
                      invalid={!!errors.files}
                      onChange={handleFileChange}
                      className="no-drag"
                      accept="image/*,application/pdf"
                    />
                    {errors.files && <h4 className="text-error mt-1">{errors.files}</h4>}
                  </FormGroup>
                </Col>
                <Col md="6" className="d-flex align-items-end">
                  <div
                    className="p-2 rounded w-100"
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      fontSize: "12px",
                      color: "#166534",
                      minHeight: "56px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <i className="fa fa-info-circle mt-1" style={{ color: "#16a34a", fontSize: "14px" }}></i>
                      <div>
                        <div><GetLanguageString props="expense_multi_file_instruction" /></div>
                        <div className="fw-bold mt-1" style={{ color: "#15803d" }}>
                          <GetLanguageString props="expense_multi_file_naming_tip" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {uploadedFiles.length > 0 && (
                <Row className="mb-3">
                  <Col md="12">
                    <div
                      className="p-3 bg-light rounded"
                      style={{ border: "1px solid #e7eaec", borderLeft: "4px solid #1ab394" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold" style={{ color: "#2f4050", fontSize: "13px" }}>
                          <i className="fa fa-paperclip me-1 text-primary"></i>
                          <GetLanguageString props="attached_files" /> ({uploadedFiles.length})
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {uploadedFiles.map((item, i) => (
                          <div
                            key={item.id || i}
                            className="d-flex align-items-center gap-3 p-2 bg-white rounded border"
                            style={{ fontSize: "13px" }}
                          >
                            <span
                              className="badge bg-secondary text-white"
                              style={{ minWidth: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              {i + 1}
                            </span>
                            <div style={{ flex: "1 1 350px", maxWidth: "550px" }}>
                              <Input
                                type="text"
                                bsSize="sm"
                                value={item.customName}
                                onChange={(e) => handleFileNameChange(i, e.target.value)}
                                placeholder="File name"
                                style={{ fontSize: "13px", height: "32px" }}
                                className="no-drag"
                              />
                            </div>
                            <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: "11.5px", fontWeight: "500" }}>
                              {(item.file.size / 1024).toFixed(1)} KB
                            </span>
                            <div className="ms-auto pe-2">
                              <TbTrash
                                size={22}
                                color="#bb0e0e"
                                style={{ cursor: "pointer" }}
                                onClick={() => removeFile(i)}
                                title="Remove file"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              <div className="ibox">
                <div className="ibox-title d-flex justify-content-between align-items-center">
                  <h5>
                    {
                      <GetLanguageString props="finance_other_expense_entry_details" />
                    }
                  </h5>
                  {hasSingleRowCategory ? (
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#94a3b8"
                      style={{ marginRight: 6, marginBottom: 1, cursor: "not-allowed", opacity: 0.5 }}
                      title={t(singleRowWarningKey)}
                    />
                  ) : (
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#3ba147"
                      onClick={addEntry}
                      style={{ marginRight: 6, marginBottom: 1, cursor: "pointer" }}
                    />
                  )}
                </div>
                <div className="ibox-content">
                  {isInvalidMultiRowState && (
                    <div
                      className="alert alert-danger mb-3 p-3 font-weight-bold"
                      style={{
                        backgroundColor: "#fef2f2",
                        color: "#991b1b",
                        border: "1px solid #f87171",
                        borderRadius: "6px",
                      }}
                    >
                      <GetLanguageString props={singleRowWarningKey} />
                    </div>
                  )}
                  {formData.entries.map((entry, index) => {
                    const categoryCode = entry.selectedCategory?.label1;
                    const isLoanIndividual =
                      categoryCode === "return_loan_individual" ||
                      categoryCode === "loan_individual";
                    const isAssetOrMaktaba = categoryCode === "asset" || categoryCode === "maktaba";
                    const isSpecial = isSpecialCategory(categoryCode);
                    const isQtyPrice = categorySupportsQtyPrice(categoryCode);

                    const isAsset = categoryCode === "asset";
                    const isMiscEquipment = isAsset && (entry.isLifeLessThan1Year || entry.isPriceLessThan10k);
                    const isConflictingRow = isInvalidMultiRowState && !isSingleRowCategory(categoryCode, entry.selectedCategory?.label);

                    return (
                      <React.Fragment key={index}>
                        {isAsset && (
                          <Row className="mb-2">
                            <Col md="12">
                              <div
                                className="d-inline-flex align-items-center mb-1"
                                style={{
                                  background: "#F8FAF9",
                                  padding: "7px 14px",
                                  borderRadius: "6px",
                                  border: "1px solid #E2E8F0",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                                }}
                              >
                                <Label
                                  check
                                  className="d-flex align-items-center mb-0 me-3"
                                  style={{
                                    cursor: "pointer",
                                    fontWeight: entry.isLifeLessThan1Year ? "700" : "500",
                                    fontSize: "13px",
                                    color: entry.isLifeLessThan1Year ? "#18A689" : "#4A5568",
                                    userSelect: "none",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Input
                                    type="checkbox"
                                    name={`isLifeLessThan1Year-${index}`}
                                    className="me-2"
                                    checked={!!entry.isLifeLessThan1Year}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        index,
                                        "isLifeLessThan1Year",
                                        e.target.checked
                                      )
                                    }
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      accentColor: "#18A689",
                                      cursor: "pointer",
                                    }}
                                  />
                                  <GetLanguageString props="item_life_less_than_1_year" />
                                </Label>

                                <div
                                  style={{
                                    width: "1px",
                                    height: "18px",
                                    background: "#CBD5E1",
                                    marginRight: "16px",
                                  }}
                                />

                                <Label
                                  check
                                  className="d-flex align-items-center mb-0"
                                  style={{
                                    cursor: "pointer",
                                    fontWeight: entry.isPriceLessThan10k ? "700" : "500",
                                    fontSize: "13px",
                                    color: entry.isPriceLessThan10k ? "#18A689" : "#4A5568",
                                    userSelect: "none",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Input
                                    type="checkbox"
                                    name={`isPriceLessThan10k-${index}`}
                                    className="me-2"
                                    checked={!!entry.isPriceLessThan10k}
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        index,
                                        "isPriceLessThan10k",
                                        e.target.checked
                                      )
                                    }
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      accentColor: "#18A689",
                                      cursor: "pointer",
                                    }}
                                  />
                                  <GetLanguageString props="item_price_less_than_10000" />
                                </Label>
                              </div>
                            </Col>
                          </Row>
                        )}
                        <Row
                          className={`mb-3 align-items-start ${isConflictingRow ? "bg-red-50 highlight-error" : ""}`}
                          style={
                            isConflictingRow
                              ? {
                                  backgroundColor: "#fef2f2",
                                  border: "1px solid #fca5a5",
                                  borderRadius: "6px",
                                  padding: "8px 4px",
                                  marginBottom: "12px",
                                }
                              : undefined
                          }
                        >
                          <Col md>
                            <CustomSelect
                              name={`category-${index}`}
                              label="finance_tab_category"
                              options={financeCategories}
                              value={entry.selectedCategory}
                              onChange={(val) =>
                                handleEntryChange(index, "selectedCategory", val)
                              }
                              error={errors.entries?.[index]?.selectedCategory}
                              placeholder={t("member_select")}
                            />
                          </Col>
                          {isLoanIndividual && (
                            <Col md>
                              <CustomInput
                                name={`serialNumber-${index}`}
                                label="notifications_rafiq_serial_number"
                                value={entry.serialNumber || ""}
                                onChange={(e) =>
                                  handleEntryChange(index, "serialNumber", e.target.value)
                                }
                                error={errors.entries?.[index]?.serialNumber}
                              />
                            </Col>
                          )}
                          {!isSpecial && (
                            <Col md>
                              <CustomSelect
                                name={`financeAccount-${index}`}
                                label="funds_expense_head"
                                options={entry.accounts || []}
                                value={entry.financeAccount}
                                onChange={(val) =>
                                  handleEntryChange(index, "financeAccount", val)
                                }
                                error={errors.entries?.[index]?.financeAccount}
                                isDisabled={!entry.selectedCategory || isMiscEquipment}
                                placeholder={
                                  !entry.selectedCategory
                                    ? t("finance_expense_heads_select")
                                    : (entry.accounts || []).length > 0
                                      ? t("finance_expense_heads_select")
                                      : t("finance_expense_heads_empty")
                                }
                              />
                            </Col>
                          )}
                          {!isSpecial && (
                            <Col md>
                              <CustomSelect
                                name={`dimension2-${index}`}
                                label="funds_expense_sub_head"
                                options={entry.dimensions || []}
                                value={entry.dimension2}
                                onChange={(val) =>
                                  handleEntryChange(index, "dimension2", val)
                                }
                                error={errors.entries?.[index]?.dimension2}
                                isDisabled={!entry.selectedCategory}
                                placeholder={
                                  !entry.selectedCategory
                                    ? t("finance_expense_sub_heads_select")
                                    : (entry.dimensions || []).length > 0
                                      ? t("finance_expense_sub_heads_select")
                                      : t("finance_expense_sub_heads_empty")
                                }
                              />
                            </Col>
                          )}
                          <Col md={isAssetOrMaktaba ? "1" : null}>
                            <CustomInput
                              name={`entryAmount-${index}`}
                              label="funds_add_fund_amount"
                              value={entry.amount}
                              onChange={(e) =>
                                handleEntryChange(index, "amount", e.target.value)
                              }
                              error={errors.entries?.[index]?.amount}
                              readOnly={isQtyPrice}
                              disabled={isQtyPrice}
                            />
                          </Col>
                          {isAssetOrMaktaba && (
                            <Col md="1">
                              <CustomInput
                                name={`quantity-${index}`}
                                label="finance_other_income_quantity"
                                value={entry.quantity}
                                onChange={(e) =>
                                  handleEntryChange(index, "quantity", e.target.value)
                                }
                                error={errors.entries?.[index]?.quantity}
                              />
                            </Col>
                          )}
                          {isAssetOrMaktaba && (
                            <Col md="1">
                              <CustomInput
                                name={`price-${index}`}
                                label="finance_other_income_price"
                                value={entry.price}
                                onChange={(e) =>
                                  handleEntryChange(index, "price", e.target.value)
                                }
                                error={errors.entries?.[index]?.price}
                              />
                            </Col>
                          )}
                          <Col md>
                            <CustomInput
                              name={`lineMemo-${index}`}
                              label="transactions_memo"
                              value={entry.lineMemo}
                              onChange={(e) =>
                                handleEntryChange(index, "lineMemo", e.target.value)
                              }
                              error={errors.entries?.[index]?.lineMemo}
                            />
                          </Col>
                          {(parseFloat(entry.amount) || 0) >= shuraThreshold && (
                            <Col md>
                              <CustomInput
                                name={`shuraNotes-${index}`}
                                label="shura_approval_notes"
                                value={entry.shuraNotes || ""}
                                onChange={(e) =>
                                  handleEntryChange(index, "shuraNotes", e.target.value)
                                }
                                error={errors.entries?.[index]?.shuraNotes}
                                placeholder={t("enter_shura_approval_notes")}
                              />
                            </Col>
                          )}
                          <Col
                            md="1"
                            className="text-center"
                          >
                            <Label className="col-form-label d-block">&nbsp;</Label>
                            {formData.entries.length === 1 ? (
                              <TbTrashOff
                                size={25}
                                color="#a72424"
                                style={{ opacity: 0.5 }}
                              />
                            ) : (
                              <TbTrash
                                size={25}
                                color="#bb0e0e"
                                style={{ cursor: "pointer" }}
                                onClick={() => removeEntry(index)}
                              />
                            )}
                          </Col>
                        </Row>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              {isOverEffectiveBalance && (
                <div
                  className="alert alert-danger d-flex align-items-center mb-3"
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    borderLeft: "4px solid #dc3545",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  <span className="me-2" style={{ fontSize: "16px" }}>⚠️</span>
                  <span>
                    Total expense (Rs {totalExpenseAmount.toLocaleString()}) exceeds your Effective Balance (Rs {effectiveCashBalance.toLocaleString()}) by Rs {(totalExpenseAmount - effectiveCashBalance).toLocaleString()}. Please adjust entry amounts before submitting.
                  </span>
                </div>
              )}
              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={
                          Object.values(loading).some(
                            (val) => val === true,
                          ) || isInvalidMultiRowState || isOverEffectiveBalance
                        }
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_expense" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(ExpenseFromCashInHand);
